<!-- This component is a partial for the EFTForm component, used to display banking fields -->
<template>
	<div class="flex flex-col gap-4">
		<div class="flex flex-col gap-4">
			<component
				:is="group ? FormKit : 'div'"
				type="group"
				:name="group"
				:key="fieldsKey"
				:value="getBlankEft()"
			>
				<CopyEftButtonList
					:active-eft="modelValue"
					@copy="onCopyButtonClick"
				/>
				<FormKit
					v-if="fieldAccountHolder"
					type="text"
					name="accountHolder"
					outer-class="col-span-4 max-w-full"
					label-class="required"
					:label="t('document.accountHolder')"
					:validation="isSepio ? 'required|length:0,21' : 'required'"
					validation-visibility="live"
				>
					<template
						v-if="isSepio"
						#help="{ value }"
					>
						<div
							v-if="value && value!.length < 22"
							class="flex"
						>
							<div class="mt-1 flex-1 text-xs text-gray-500">
								Maximum length of 21 characters
							</div>
							<div class="mt-1 text-right text-xs text-gray-500">
								{{ value.length }} / 21
							</div>
						</div>
						<div
							v-else-if="value && value!.length >= 22"
							class="flex"
						>
							<div class="mt-1 flex-1 text-xs text-red-600">
								Sepio requires the account holder name to be a maximum of 21
								characters. Abbreviations are acceptable (ex: M. Smitherson)
							</div>
							<div class="mt-1 text-xs whitespace-nowrap text-red-600">
								{{ value?.length || 0 }} / 21
							</div>
						</div>
					</template>
					<template
						v-if="isSepio"
						#messages
					>
						<!-- Empty - no validation messages shown -->
						<span></span>
					</template>
				</FormKit>
				<div class="flex gap-11">
					<div class="flex grow flex-row gap-5 *:w-full *:max-w-none">
						<FormKit
							v-if="fieldBankName"
							type="text"
							name="bankName"
							:label="t('document.bankName')"
							input-class="w-full"
							label-class="required"
							validation="required"
						/>
					</div>
					<div class="grid grid-cols-1 *:w-full *:max-w-none">
						<FormKit
							type="radio"
							decorator-icon="StopIcon"
							decorator-icon-class="max-w-none"
							decorator-class="border-gray-400! rounded-md h-6 w-6!"
							:label="t('document.accountType')"
							name="accountType"
							legend-class="select-none required"
							options-class="flex gap-8 py-1.5"
							validation="required"
							:options="[
								{ label: t('document.checking'), value: 'checking' },
								{ label: t('document.savings'), value: 'savings' },
							]"
						/>
					</div>
				</div>
				<div class="grid grid-cols-2 gap-5 *:w-full *:max-w-none">
					<FormKit
						type="text"
						name="routingNumber"
						:label="t('document.routingNumber')"
						label-class="required"
						validation="required|number|length:9,9"
						validation-visibility="live"
						maxlength="9"
					/>
					<FormKit
						type="text"
						name="routingNumber_confirm"
						:label="t('document.confirmRoutingNumber')"
						label-class="required"
						validation="required|confirm"
					/>
				</div>
				<div class="grid grid-cols-2 gap-5 *:w-full *:max-w-none">
					<FormKit
						type="text"
						name="accountNumber"
						:label="t('document.accountNumber')"
						label-class="required"
						validation="required|number|length:5,17"
						maxlength="17"
						validation-visibility="live"
					/>
					<FormKit
						type="text"
						name="accountNumber_confirm"
						:label="t('document.confirmAccountNumber')"
						label-class="required"
						validation="required|confirm"
					/>
				</div>
			</component>
		</div>
		<img
			v-if="showCheck"
			:src="CheckImage"
			alt="Check"
			:class="fullWidthCheck ? 'w-full transform' : 'w-3/5 transform'"
		/>
	</div>
</template>

<script setup lang="ts">
import CopyEftButtonList from './CopyEftButtonList.vue'
import CheckImage from '@/assets/check_image.png'
import { usePresentationLocale } from '@/i18n'
import { getBlankEft, isBlankEft, toFlatEft } from '@/mixins/eft'
import type { EFT } from '@/types'
import { FormKit } from '@formkit/vue'
import { ref, toRaw, watch } from 'vue'

const { t } = usePresentationLocale()

/** The eft object. Needed for copy buttons. */
const modelValue = defineModel<EFT | undefined>()

const fieldsKey = ref(0)

// Remount when the EFT is cleared. Do not remount on copy — destroying
// FormKit inputs makes the parent form drop each key, then recreate them
// as undefined.
watch(
	() => modelValue.value,
	(newVal, oldVal) => {
		if (!newVal || !oldVal || newVal === oldVal) {
			return
		}

		const becameBlank = isBlankEft(newVal) && !isBlankEft(oldVal)

		if (becameBlank) {
			fieldsKey.value++
		}
	}
)

const props = defineProps<{
	/** Wrap inputs in a FormKit group? */
	group?: string

	/** Include account holder field? */
	fieldAccountHolder?: boolean

	/** Is this being rendered for the Sepio step? */
	isSepio?: boolean

	/** Include bank name field? */
	fieldBankName?: boolean

	showCheck?: boolean

	/** Display check at full width? */
	fullWidthCheck?: boolean
}>()

function onCopyButtonClick(eft: EFT) {
	modelValue.value = toFlatEft(structuredClone(toRaw(eft)))
}
</script>

<style scoped>
@reference "tailwindcss";
:deep([data-checked='true'] .formkit-icon) {
	@apply text-green-400;
}

:deep(.formkit-label.required::after),
:deep(.formkit-legend.required::after) {
	@apply ml-1.5 text-red-500 content-['*'];
}
</style>
